/**
 * DCS catalog schemas for bridge witnesses and bridge receipts.
 *
 * The lifecycle logic lives in bridge-witnesses.ts; this module owns only the
 * JSON boundary used by structured catalogs.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { z } from "zod";

import { NEUTRALIZATION_THEOREM_CODES } from "./types.js";
import {
  defineBridgeWitness,
  isFeedbackFreeTrace,
  isSeamNormalFormTrace,
} from "./bridge-witnesses.js";
import type { BridgeWitness } from "./bridge-witnesses.js";

const theoremCodeSchema = z.enum(NEUTRALIZATION_THEOREM_CODES);
const seamNormalFormTraceSchema = z.object({
  phases: z.tuple([z.literal("B1"), z.literal("B2"), z.literal("B3"), z.literal("B4")]),
});

/** Schema for a witness row in a DCS JSON catalog (theorem-keyed). */
export const bridgeWitnessRowSchema = z
  .object({
    id: z.string().min(1),
    theorem: theoremCodeSchema,
    issuer: z.string().min(1),
    scope: z.string().min(1),
    issuedAt: z.number(),
    expiresAt: z.number().nullable().optional(),
    receiptAnchor: z.string().nullable().optional(),
    revoked: z.boolean().optional(),
  })
  .loose();

/** A validated witness catalog row. */
export type BridgeWitnessRow = z.infer<typeof bridgeWitnessRowSchema>;

/** Schema for a bridge receipt persisted to a DCS catalog. */
export const bridgeReceiptSchema = z
  .object({
    bridgeId: z.string().min(1),
    witnessId: z.string().min(1),
    theorem: theoremCodeSchema,
    consumedResources: z.array(z.string()),
    trace: seamNormalFormTraceSchema,
    previousAnchor: z.string().nullable(),
    nextAnchor: z.string().nullable(),
  })
  .loose()
  .refine((row) => isFeedbackFreeTrace(row.trace) && isSeamNormalFormTrace(row.trace), {
    message: "receipt trace must be the complete feedback-free B1→B2→B3→B4 sequence",
  });

/** A validated bridge-receipt catalog row. */
export type BridgeReceiptRow = z.infer<typeof bridgeReceiptSchema>;

/** Parse a witness catalog row, returning `null` on failure. */
export function parseBridgeWitnessRow(value: unknown): BridgeWitnessRow | null {
  const result = bridgeWitnessRowSchema.safeParse(value);
  return result.success ? result.data : null;
}

/** Parse a bridge-receipt row, returning `null` on failure. */
export function parseBridgeReceiptRow(value: unknown): BridgeReceiptRow | null {
  const result = bridgeReceiptSchema.safeParse(value);
  return result.success ? result.data : null;
}

/** Build a {@link BridgeWitness} from a validated DCS catalog row. */
export function bridgeWitnessFromRow(row: BridgeWitnessRow): BridgeWitness {
  return defineBridgeWitness({
    id: row.id,
    theorem: row.theorem,
    issuer: row.issuer,
    scope: row.scope,
    issuedAt: row.issuedAt,
    expiresAt: row.expiresAt ?? null,
    receiptAnchor: row.receiptAnchor ?? null,
    revoked: row.revoked ?? false,
  });
}
